<template>
  <main class="">
    <PageHeader
      title="My Comments"
      subTitle="Comments you've made across your courses"
    />
    <div class="flex mx-auto  flex-col gap-5 bg-white p-4 sm:p-6 lg:px-8">
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      >
        <div class="w-full sm:w-[200px]">
          <e-combobox
            class="flex-1"
            v-model="sort"
            :options="options"
            :clearable="true"
            placeholder="Sort By"
          ></e-combobox>
        </div>
      </div>

      <section class="flex flex-col gap-4" v-if="loading">
        <div
          v-for="n in 3"
          :key="n"
          class="border border-untitled-gray-200 rounded-xl p-4 sm:p-5"
        >
          <el-skeleton :rows="3" animated />
        </div>
      </section>

      <section
        v-else-if="notes?.length"
        @scroll="onScroll"
        class="flex flex-col gap-4 max-h-[65vh] overflow-y-auto pr-2"
      >
        <v4-note-card
          v-for="note in notes"
          :key="note[0]?.id"
          :groupedNote="note"
        />
        <div
          v-if="loadingMore"
          class="border border-untitled-gray-200 rounded-xl p-4 sm:p-5"
        >
          <el-skeleton :rows="2" animated />
        </div>
      </section>

      <section
        v-else
        class="flex flex-col items-center gap-2 py-12 text-center"
      >
        <ChatAlt2Icon class="w-10 h-10 text-untitled-gray-300" />
        <p class="text-untitled-gray-500 font-medium">No comments found.</p>
        <p class="text-sm text-untitled-gray-400">
          Comments you leave on course units will show up here.
        </p>
      </section>
    </div>
  </main>
</template>

<script setup>
import { ref, inject, onMounted, computed, watch } from "vue";
import { ElSkeleton } from "element-plus";
import { ChatAlt2Icon } from "@heroicons/vue/solid";
import V4NoteCard from "@/components/notes/V4NoteCard.vue";
import Bugsnag from "@bugsnag/js";
import eventBus from "@/plugins/eventBus";
import ECombobox from "@/components/element-components/ECombobox";
import PageHeader from "@/layout/components/PageHeader.vue";

const axios = inject("axios");

const loading = ref(true);
const loadingMore = ref(false);
const notes = ref([]);
const pageLimit = ref(10);
const currentPage = ref(1);
const totalPage = ref(-1);
const sort = ref();
const options = ["A-Z", "Z-A"];

const onScroll = ({ target: { scrollTop, clientHeight, scrollHeight } }) => {
  if (
    scrollTop + clientHeight >= scrollHeight - 5 &&
    currentPage.value < totalPage.value
  ) {
    currentPage.value++;
    loadingMore.value = true;
    fetchNotes();
  }
};

const fetchNotes = async () => {
  axios
    .get("/api/v3/user/notes", {
      params: {
        page: currentPage.value,
        limit: pageLimit.value,
        sort: sortedBy?.value?.sort ?? "notes.created_at",
        sortBy: sortedBy?.value?.sortBy ?? "desc",
      },
    })
    .then(({ data }) => {
      notes.value = [...notes.value, ...data?.data];
      totalPage.value = data?.last_page;
      loading.value = false;
      loadingMore.value = false;
    })
    .catch((error) => {
      loading.value = false;
      loadingMore.value = false;
      Bugsnag.notify(error);
    });
};

const sortedBy = computed(() => {
  switch (sort.value) {
    case "A-Z":
      return {
        sort: "units.name",
        sortBy: "asc",
      };
    case "Z-A":
      return {
        sort: "units.name",
        sortBy: "desc",
      };
    default:
      return null;
  }
});

watch(sortedBy, () => {
  loading.value = true;
  currentPage.value = 1;
  notes.value = [];
  fetchNotes();
});

onMounted(() => {
  loading.value = true;

  fetchNotes();

  eventBus.$on("DELETE_NOTE", (id) => {
    let counter = -1;

    const index = notes.value.findIndex((note) => {
      const noteIndex = note.findIndex((item) => item.id === id);

      counter = noteIndex;
      return noteIndex !== -1;
    });

    //Removing Main Card
    if (notes.value[index].length === 1) {
      notes.value.splice(index, 1);
    }
    //Removing Sub Card
    else {
      notes.value[index].splice(counter, 1);
    }
  });

  eventBus.$on("UPDATE_NOTE", (note) => {
    const index = notes.value.findIndex((itemNote) => {
      const noteIndex = itemNote.findIndex(
        (itemSubNote) => itemSubNote.id === note[0].id
      );

      return noteIndex !== -1;
    });

    notes.value.splice(index, 1, note);
  });
});
</script>
